import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free', color: 'bg-gray-100 text-gray-700' },
  { value: 'professional', label: 'Professional', color: 'bg-blue-100 text-blue-700' },
  { value: 'enterprise', label: 'Enterprise', color: 'bg-purple-100 text-purple-700' },
  { value: 'demo', label: 'Demo (Full Access)', color: 'bg-amber-100 text-amber-700' },
];

const getPlanBadge = (plan) => {
  const found = PLAN_OPTIONS?.find((p) => p?.value === plan?.toLowerCase());
  return found || { value: plan, label: plan, color: 'bg-gray-100 text-gray-700' };
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const GenerateUserModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    email: '',
    password: '',
    fullName: '',
    companyName: '',
    planType: 'demo',
    note: '',
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (!form?.email?.trim()) { setError('Email is required.'); return; }
    if (!form?.password || form?.password?.length < 6) { setError('Password must be at least 6 characters.'); return; }

    setLoading(true);
    try {
      // Save admin session
      const { data: { session: adminSession } } = await supabase?.auth?.getSession();
      const adminEmail = adminSession?.user?.email;

      const { data, error: signUpError } = await supabase?.auth?.signUp({
        email: form?.email?.trim()?.toLowerCase(),
        password: form?.password,
        options: {
          data: {
            full_name: form?.fullName?.trim() || null,
            company_name: form?.companyName?.trim() || null,
            plan_type: form?.planType,
          },
          emailRedirectTo: `${window?.location?.origin}/authentication`,
        },
      });

      if (signUpError) throw signUpError;

      if (data?.user) {
        const { error: profileError } = await supabase?.from('user_profiles')?.upsert({
          id: data?.user?.id,
          email: form?.email?.trim()?.toLowerCase(),
          full_name: form?.fullName?.trim() || null,
          company_name: form?.companyName?.trim() || null,
          plan_type: form?.planType,
          tier: form?.planType,
          subscription_status: 'active',
          is_test_user: true,
          test_user_note: form?.note?.trim() || null,
          updated_at: new Date()?.toISOString(),
        }, { onConflict: 'id' });

        if (profileError) {
          console.warn('Profile upsert warning:', profileError?.message);
        }
      }

      // Restore admin session if displaced
      const { data: { session: currentSession } } = await supabase?.auth?.getSession();
      const wasSignedOut = !currentSession || currentSession?.user?.email !== adminEmail;
      if (wasSignedOut && adminSession?.refresh_token) {
        try {
          await supabase?.auth?.setSession({
            access_token: adminSession?.access_token,
            refresh_token: adminSession?.refresh_token,
          });
        } catch (restoreErr) {
          console.warn('Could not restore admin session:', restoreErr?.message);
        }
      }

      setSuccess(`Test user "${form?.email}" created with ${form?.planType} plan.`);
      setTimeout(() => { onSuccess?.(); onClose?.(); }, 1800);
    } catch (err) {
      setError(err?.message || 'Failed to create test user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-elevation-lg w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="UserPlus" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Generate Test User</h3>
              <p className="text-xs text-muted-foreground">Create a verified test account with a specific plan</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-smooth p-1 rounded-md hover:bg-muted">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <Icon name="AlertCircle" size={15} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
              <Icon name="CheckCircle2" size={15} className="mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input label="Full Name" placeholder="Jane Tester" value={form?.fullName} onChange={(e) => handleChange('fullName', e?.target?.value)} />
            <Input label="Company / Label" placeholder="QA Team" value={form?.companyName} onChange={(e) => handleChange('companyName', e?.target?.value)} />
          </div>

          <Input label="Email Address *" type="email" placeholder="test@example.com" value={form?.email} onChange={(e) => handleChange('email', e?.target?.value)} required />

          <Input label="Password *" type="password" placeholder="Min. 6 characters" value={form?.password} onChange={(e) => handleChange('password', e?.target?.value)} required />

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Plan Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {PLAN_OPTIONS?.map((plan) => (
                <button
                  key={plan?.value}
                  type="button"
                  onClick={() => handleChange('planType', plan?.value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-smooth ${
                    form?.planType === plan?.value
                      ? 'border-primary bg-primary/5 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full ${form?.planType === plan?.value ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                  {plan?.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Internal Note <span className="text-muted-foreground font-normal">(optional)</span></label>
            <textarea
              value={form?.note}
              onChange={(e) => handleChange('note', e?.target?.value)}
              placeholder="e.g. Used for onboarding video, demo session on 2026-08-10"
              rows={2}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" type="button" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" loading={loading}>
              <Icon name="UserPlus" size={15} className="mr-1.5" />
              Generate Test User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditPlanModal = ({ account, onClose, onSuccess }) => {
  const [planType, setPlanType] = useState(account?.plan_type || account?.planType || 'free');
  const [note, setNote] = useState(account?.test_user_note || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const { error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({
          plan_type: planType,
          tier: planType,
          test_user_note: note?.trim() || null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', account?.id);

      if (updateError) throw updateError;
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to update plan.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-elevation-lg w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div>
            <h3 className="text-base font-semibold text-foreground">Edit Test User</h3>
            <p className="text-xs text-muted-foreground truncate max-w-xs">{account?.email}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-smooth p-1 rounded-md hover:bg-muted">
            <Icon name="X" size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <Icon name="AlertCircle" size={15} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Plan Type</label>
            <div className="grid grid-cols-2 gap-2">
              {PLAN_OPTIONS?.map((plan) => (
                <button
                  key={plan?.value}
                  type="button"
                  onClick={() => setPlanType(plan?.value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-smooth ${
                    planType === plan?.value
                      ? 'border-primary bg-primary/5 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full ${planType === plan?.value ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                  {plan?.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Internal Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e?.target?.value)}
              rows={2}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              placeholder="Internal note about this test account"
            />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" type="button" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button onClick={handleSave} loading={loading}>Save Changes</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ account, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      // Mark as deleted / remove test flag — actual auth deletion requires service role
      const { error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({
          is_test_user: false,
          subscription_status: 'inactive',
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', account?.id);

      if (updateError) throw updateError;
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to remove test user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-elevation-lg w-full max-w-sm mx-4 overflow-hidden">
        <div className="p-6 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Trash2" size={22} className="text-red-600" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1">Remove Test User</h3>
          <p className="text-sm text-muted-foreground mb-1">This will deactivate the test account for:</p>
          <p className="text-sm font-medium text-foreground mb-4 truncate">{account?.email}</p>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white border-red-600"
              onClick={handleDelete}
              loading={loading}
            >
              Remove
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TestUserManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [testUsers, setTestUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!document.documentElement?.style?.getPropertyValue('--sidebar-width')) {
      document.documentElement?.style?.setProperty('--sidebar-width', '256px');
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/authentication'); return; }
    const ownerEmail = import.meta.env?.VITE_SUPER_ADMIN_EMAIL;
    const adminCheck = ownerEmail && user?.email?.toLowerCase()?.trim() === ownerEmail?.toLowerCase()?.trim();
    if (!adminCheck) { navigate('/super-admin-dashboard'); return; }
    setIsAdmin(true);
    loadTestUsers();
  }, [user, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTestUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('is_test_user', true)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setTestUsers(data || []);
    } catch (err) {
      console.error('Error loading test users:', err);
      setTestUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCopyEmail = (email, id) => {
    navigator?.clipboard?.writeText(email);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const filteredUsers = testUsers?.filter((u) => {
    const matchSearch = !search || u?.email?.toLowerCase()?.includes(search?.toLowerCase()) || u?.full_name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchPlan = filterPlan === 'all' || u?.plan_type === filterPlan;
    return matchSearch && matchPlan;
  });

  const planCounts = PLAN_OPTIONS?.reduce((acc, p) => {
    acc[p?.value] = testUsers?.filter((u) => u?.plan_type === p?.value)?.length;
    return acc;
  }, {});

  if (!isAdmin && !authLoading) return null;

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar stats={{}} />
      <div
        className="pt-16 transition-all duration-300"
        style={{ marginLeft: 'var(--sidebar-width, 256px)' }}
      >
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => navigate('/super-admin-dashboard')}
                  className="text-muted-foreground hover:text-foreground transition-smooth text-sm flex items-center gap-1"
                >
                  <Icon name="ChevronLeft" size={15} />
                  Super Admin
                </button>
              </div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="FlaskConical" size={22} className="text-primary" />
                </div>
                Test User Management
              </h1>
              <p className="text-muted-foreground mt-1 text-sm">
                Generate and manage verified test accounts with specific plan types for demos, QA, and screenshots.
              </p>
            </div>
            <Button onClick={() => setShowGenerate(true)} className="flex items-center gap-2 shrink-0">
              <Icon name="UserPlus" size={16} />
              Generate Test User
            </Button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {PLAN_OPTIONS?.map((plan) => (
              <div key={plan?.value} className="bg-card border border-border rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1 capitalize">{plan?.label}</p>
                <p className="text-2xl font-bold text-foreground">{planCounts?.[plan?.value] || 0}</p>
                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${plan?.color}`}>{plan?.value}</span>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <div className="relative flex-1">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by email or name…"
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterPlan('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth border ${filterPlan === 'all' ? 'bg-primary text-white border-primary' : 'bg-background border-border text-muted-foreground hover:text-foreground'}`}
              >
                All ({testUsers?.length})
              </button>
              {PLAN_OPTIONS?.map((plan) => (
                <button
                  key={plan?.value}
                  onClick={() => setFilterPlan(plan?.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth border ${filterPlan === plan?.value ? 'bg-primary text-white border-primary' : 'bg-background border-border text-muted-foreground hover:text-foreground'}`}
                >
                  {plan?.label} ({planCounts?.[plan?.value] || 0})
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              </div>
            ) : filteredUsers?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mb-4">
                  <Icon name="FlaskConical" size={24} className="text-muted-foreground" />
                </div>
                <p className="text-foreground font-medium mb-1">No test users found</p>
                <p className="text-muted-foreground text-sm mb-4">
                  {search || filterPlan !== 'all' ? 'Try adjusting your filters.' : 'Generate your first test user to get started.'}
                </p>
                {!search && filterPlan === 'all' && (
                  <Button onClick={() => setShowGenerate(true)} size="sm">
                    <Icon name="UserPlus" size={14} className="mr-1.5" />
                    Generate Test User
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Plan</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Note</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Created</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredUsers?.map((u) => {
                      const badge = getPlanBadge(u?.plan_type);
                      return (
                        <tr key={u?.id} className="hover:bg-muted/20 transition-smooth">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <span className="text-xs font-semibold text-primary">
                                  {(u?.full_name || u?.email || '?')?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0">
                                {u?.full_name && <p className="font-medium text-foreground truncate">{u?.full_name}</p>}
                                <div className="flex items-center gap-1.5">
                                  <p className="text-muted-foreground text-xs truncate max-w-[180px]">{u?.email}</p>
                                  <button
                                    onClick={() => handleCopyEmail(u?.email, u?.id)}
                                    className="text-muted-foreground hover:text-primary transition-smooth shrink-0"
                                    title="Copy email"
                                  >
                                    <Icon name={copiedId === u?.id ? 'Check' : 'Copy'} size={12} />
                                  </button>
                                </div>
                                {u?.company_name && <p className="text-xs text-muted-foreground/70 truncate">{u?.company_name}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge?.color}`}>
                              {badge?.label}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 hidden md:table-cell">
                            <p className="text-xs text-muted-foreground max-w-[200px] truncate">
                              {u?.test_user_note || <span className="italic opacity-50">No note</span>}
                            </p>
                          </td>
                          <td className="px-4 py-3.5 hidden sm:table-cell">
                            <p className="text-xs text-muted-foreground">{formatDate(u?.created_at)}</p>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => setEditTarget(u)}
                                className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-smooth"
                                title="Edit plan"
                              >
                                <Icon name="Pencil" size={14} />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(u)}
                                className="p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-smooth"
                                title="Remove test user"
                              >
                                <Icon name="Trash2" size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Info Banner */}
          <div className="mt-4 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <Icon name="Info" size={15} className="mt-0.5 shrink-0 text-amber-600" />
            <p>
              Test users are real accounts flagged with <code className="bg-amber-100 px-1 rounded text-xs">is_test_user = true</code>.
              They can log in normally and are intended for demos, QA, and screenshots only.
              Removing a test user deactivates the account but does not delete auth credentials.
            </p>
          </div>
        </div>
      </div>

      {showGenerate && (
        <GenerateUserModal
          onClose={() => setShowGenerate(false)}
          onSuccess={loadTestUsers}
        />
      )}
      {editTarget && (
        <EditPlanModal
          account={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={loadTestUsers}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          account={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onSuccess={loadTestUsers}
        />
      )}
    </div>
  );
};

export default TestUserManagement;
